/**
 * api-news.js  —  Kili AI Assistant · News Segment API Layer
 *
 * Add to your existing api.js or import separately in handlers-news.js.
 * Uses the same AkiliPortal.apiFetch pattern used throughout the portal.
 */

const NEWS_CACHE_TTL_MS = 90_000; // 90 s — don't re-hit API if data is fresh

/** In-memory response cache keyed by request fingerprint */
const _cache = new Map<string, { data: any; ts: number }>(); // key → { data, ts }

/**
 * Build the cache key from params.
 */
function _cacheKey(params: Record<string, any>): string {
  return JSON.stringify(params);
}

interface FetchOptions extends RequestInit {
  headers?: Record<string, string>;
}

/**
 * Core fetch helper — mirrors AkiliPortal.apiFetch convention.
 * Falls back to raw fetch() if AkiliPortal is unavailable (e.g. standalone test).
 */
async function _apiFetch(path: string, opts: FetchOptions = {}): Promise<any> {
  if ((window as any).AkiliPortal?.apiFetch) {
    // FIX: AkiliPortal.apiFetch returns a Response object, not parsed JSON.
    return (window as any).AkiliPortal.apiFetch(path, opts).then((r: Response) => r.json());
  }
  // Raw fallback
  const base = (window as any).__AKILI_API_BASE__ ?? '';
  const res = await fetch(`${base}${path}`, {
    ...opts,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(opts.headers ?? {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `HTTP ${res.status}`);
  }
  return res.json();
}

export interface Article {
  id: string;
  title: string;
  source_name: string;
  doc_type: string;
  region: string;
  published_at: number;
  ingested_at: number;
  summary: string | null;
  source_url: string;
  content?: string;
}

export interface NewsParams {
  limit?: number;
  offset?: number;
  region?: 'all' | 'kenya' | 'africa' | 'global';
  docType?: 'all' | 'news' | 'press_release' | 'economic_report' | 'gazette' | 'hansard';
  nocache?: boolean;
}

/**
 * Fetch a paginated list of awareness articles.
 *
 * GET /api/ai/news?limit=20&offset=0&region=kenya&docType=all
 */
export async function getNews(params: NewsParams = {}): Promise<{ articles: Article[]; total: number }> {
  const {
    limit   = 20,
    offset  = 0,
    region  = 'all',
    docType = 'all',
    nocache = false,
  } = params;

  const key = _cacheKey({ limit, offset, region, docType });
  if (!nocache) {
    const cached = _cache.get(key);
    if (cached && Date.now() - cached.ts < NEWS_CACHE_TTL_MS) {
      return cached.data;
    }
  }

  const qs = new URLSearchParams({
    limit:  String(limit),
    offset: String(offset),
  });
  if (region  !== 'all') qs.set('region',  region);
  if (docType !== 'all') qs.set('docType', docType);

  const data = await _apiFetch(`/api/ai/news?${qs.toString()}`);

  _cache.set(key, { data, ts: Date.now() });
  return data;
}

/**
 * Fetch a single article by ID (for the inline preview drawer).
 * GET /api/ai/news/:id
 */
export async function getNewsArticle(id: string): Promise<Article> {
  const key = `article:${id}`;
  const cached = _cache.get(key);
  if (cached && Date.now() - cached.ts < NEWS_CACHE_TTL_MS * 5) {
    return cached.data;
  }
  const data = await _apiFetch(`/api/ai/news/${encodeURIComponent(id)}`);
  _cache.set(key, { data, ts: Date.now() });
  return data;
}

/**
 * Check for breaking-news articles ingested in the last hour.
 * GET /api/ai/news/breaking  →  { breaking: boolean, article?: Article }
 */
export async function checkBreakingNews(): Promise<{ breaking: boolean; article?: Article }> {
  try {
    return await _apiFetch('/api/ai/news/breaking');
  } catch {
    return { breaking: false };
  }
}

/**
 * Invalidate the entire news cache (e.g. after manual refresh).
 */
export function invalidateNewsCache(): void {
  for (const key of _cache.keys()) {
    if (!key.startsWith('article:')) _cache.delete(key);
  }
}